import {
    useEffect,
    useState,
} from 'react';

interface InfiniteScrollOptions {
    hasMore: boolean;
    loadMore: () => void;
}

/**
 * Callback ref rather than a `RefObject`: every caller mounts the sentinel conditionally — behind a
 * `Collapse unmountOnExit`, a `Popover`, a loading flag — and a ref object gives the effect nothing
 * to re-run on, so the observer keeps watching the detached node and paging stops for good.
 */
export function useInfiniteScrollSentinel({
    hasMore, loadMore,
}: InfiniteScrollOptions): (node: HTMLDivElement | null) => void {
    const [ sentinel, setSentinel ] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!sentinel || !hasMore || typeof IntersectionObserver === 'undefined') {
            return undefined;
        }

        const observer = new IntersectionObserver(entries => {
            if (entries[0]?.isIntersecting) {
                loadMore();
            }
        });

        observer.observe(sentinel);

        return () => observer.disconnect();
    }, [ hasMore, loadMore, sentinel ]);

    return setSentinel;
}
